import { useMobile } from '@/hooks/useMobile'
import { useMaybeModule, useModules } from '@/hooks/useModule'
import { declareComponent } from '@/registry'
import { boardModule } from '@/routes'
import { setBoardHeight } from '@/stores/temp'
import { throttle } from '@lc/libutils'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Switch } from 'wouter'
import { NullLoading } from '../ui/Loading'
import styles from './Board.scss'
import { ModeSelector } from './ModeSelector'
import { ModuleSelector } from './ModuleSelector'

export const SearchBar = /*#__PURE__*/ declareComponent(
	'searchbar',
	'module-searchbar',
	() => import(/* webpackChunkName: 'module-searchbar' */ './SearchBar'),
	NullLoading,
)

export function Board() {
	const modules = useModules()
	const currentModule = useMaybeModule()
	const mobile = useMobile()
	const dispatch = useDispatch()

	const boardRef = useRef<HTMLDivElement>(null)
	const startYRef = useRef<number | null>(null)
	const startHeightRef = useRef<number>(null)

	let first = false
	let last = false
	if (currentModule) {
		const modulesForMode = modules.filter(
			module => module.mode === currentModule.mode,
		)
		const idx = modulesForMode.indexOf(currentModule)
		first = idx === 0
		last = idx === modulesForMode.length - 1
	}

	const handleDrag = useCallback((e: React.MouseEvent | React.TouchEvent) => {
		const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

		startYRef.current = clientY
		startHeightRef.current =
			boardRef.current!.getBoundingClientRect().height

		const handleMouseMove = (e: MouseEvent | TouchEvent) => {
			const currentY = 'touches' in e ? e.touches[0].clientY : e.clientY
			const diff = startYRef.current! - currentY
			let newHeight = startHeightRef.current! + diff

			newHeight = Math.max(
				window.innerHeight * 0.25,
				Math.min(window.innerHeight * 0.77, newHeight),
			)
			boardRef.current!.style.height = newHeight + 'px'
		}

		const controller = new AbortController()
		const signal = controller.signal

		const handleMouseUp = () => {
			controller.abort()
			startYRef.current = null
		}

		if ('touches' in e) {
			document.addEventListener('touchmove', handleMouseMove, { signal })
			document.addEventListener('touchend', handleMouseUp, { signal })
		} else {
			document.addEventListener('mousemove', handleMouseMove, { signal })
			document.addEventListener('mouseup', handleMouseUp, { signal })
		}
	}, [])

	useEffect(() => {
		if (!boardRef.current) return
		const observe = throttle(() => {
			if (!boardRef.current) return
			const height = boardRef.current.getBoundingClientRect().height
			dispatch(setBoardHeight(height))
		}, 50)
		const ih = new ResizeObserver(observe)
		ih.observe(boardRef.current)
		observe()
		return () => ih.disconnect()
	}, [])

	return (
		<>
			{mobile && !!currentModule && <SearchBar />}
			<div
				ref={boardRef}
				className={styles.container}
				data-first-module={first || undefined}
				data-last-module={last || undefined}
			>
				{mobile && (
					<div
						className={styles.dragHandle}
						onMouseDown={handleDrag}
						onTouchStart={handleDrag}
						role="button"
						tabIndex={-1}
					>
						<span className={styles.dragSpan}></span>
					</div>
				)}
				{!mobile && !!currentModule && <SearchBar />}
				<div className={styles.content}>
					<ModuleSelector />
					<Switch key={currentModule?.path ?? '/'}>
						{modules.map(boardModule)}
					</Switch>
					<ModeSelector />
				</div>
			</div>
		</>
	)
}
